import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { CARD_TRIAL_FLAG } from '@/lib/card-trial';
import { redirectToStripeCheckout } from '@/lib/stripe-redirect';
import { useActivateTrialMutation } from '@/store/features/billing.feature';

/**
 * The way into a plan a page or a Clu card needs, one decision for both
 * (PlanGate, PlanRequiredCard): the Pro trial when the workspace can still
 * start it and the trial includes the step, an AppSumo plan's own way in,
 * the upgrade otherwise. `v4`: the workspace is on the v4 catalog, whose
 * prices the plans table carries.
 */
export function usePlanGateRoute({
  plan,
  notInTrial = false,
  appSumo: appSumoProp = false,
}: {
  plan: string;
  notInTrial?: boolean;
  appSumo?: boolean;
}): { route: 'trial' | 'appsumo' | 'upgrade'; v4: boolean } {
  const { trial, catalogFamily, isAppSumoOnly } = useFeatureAccess();
  // The card trial's switch, read fail-closed as the trial banner reads it:
  // off, no gate offers the trial either.
  const cardTrialEnabled = useFeatureFlagEnabled(CARD_TRIAL_FLAG) === true;
  const appSumo = appSumoProp || isAppSumoOnly === true;
  const v4 = catalogFamily === 'v4';
  // A card-required trial on offer that hasn't started: Checkout, from here.
  const trialOffered =
    cardTrialEnabled &&
    !appSumo &&
    !notInTrial &&
    plan.trim().toLowerCase() !== 'enterprise' &&
    trial?.card_required === true &&
    !trial.started_at;
  return {
    route: trialOffered ? 'trial' : appSumo ? 'appsumo' : 'upgrade',
    v4,
  };
}

/**
 * Starts the card-required Pro trial: activates it for the workspace and
 * opens Stripe Checkout. True once Checkout opens; false when it didn't
 * (another workspace took over, or it failed, with a toast for this
 * workspace). `onBegin` gets the workspace the attempt is for.
 */
export function useStartCardTrial() {
  const { trial, organizationId } = useFeatureAccess();
  const posthog = usePostHog();
  const { beginOrganizationAction, isOrganizationActionCurrent } =
    useOrganizationActionFence(organizationId);
  const [activateTrial] = useActivateTrialMutation();
  const trialPlan = trial?.plan ?? 'pro';

  return useCallback(
    async (onBegin?: (organizationId: string) => void): Promise<boolean> => {
      const action = beginOrganizationAction();
      if (!action) return false;
      onBegin?.(action.organizationId);
      bestEffortAnalytics(() =>
        posthog?.capture('card_trial_start_clicked', {
          source: 'upgrade_card',
          trial_plan: trialPlan,
        })
      );
      try {
        const response = await activateTrial({
          organizationId: action.organizationId,
        }).unwrap();
        return await redirectToStripeCheckout(response, () =>
          isOrganizationActionCurrent(action)
        );
      } catch (error) {
        if (isOrganizationActionCurrent(action))
          toast.error(
            (error as { data?: { detail?: string } })?.data?.detail ||
              'Could not open checkout. Please try again.'
          );
        return false;
      }
    },
    [
      activateTrial,
      beginOrganizationAction,
      isOrganizationActionCurrent,
      posthog,
      trialPlan,
    ]
  );
}
